import { Button, Card, Col, Row, Spin, Table, Tag } from 'antd'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  getDelegatedClaimsDetail,
  getDelegatedClaimsDocument,
} from '../../api/delegatedClaims.api'

const DelegatedClaimsDetailPage = () => {
  const navigate = useNavigate()
  const { delegationId } = useParams()
  const [delegationInfo, setDelegationInfo] = useState(null)
  const [prescriptionList, setPrescriptionList] = useState([])
  const [documentData, setDocumentData] = useState(null)
  const [loading, setLoading] = useState(true)

  // 나이 계산 함수 (19세 기준)
  const calculateAgeCategory = (birthday) => {
    if (!birthday) return '-'
    const year = '19' + birthday.substring(0, 2)
    const month = birthday.substring(2, 4)
    const day = birthday.substring(4, 6)
    const birthDate = dayjs(`${year}-${month}-${day}`)
    const age = dayjs().diff(birthDate, 'year')
    return age >= 19 ? '19세 이상' : '19세 미만'
  }

  // 배송 상태 매핑 함수
  const getDeliveryStatus = (status) => {
    const statusMap = {
      SUBMITTED: '처방전 제출',
      PURCHASED: '배송중',
      ADMIN_APPROVED: '배송중',
      ADMIN_REJECTED: '반려',
      REJECTED: '공단 반려',
      FINALIZED: '배송완료',
    }
    return statusMap[status] || '대기'
  }

  // 위임 정보 및 처방전 목록 조회
  useEffect(() => {
    const fetchDelegationDetail = async () => {
      if (!delegationId) return

      try {
        setLoading(true)

        const response = await getDelegatedClaimsDetail({ id: delegationId })
        // console.log('response', response)

        // 위임장 문서 정보 조회
        const documentResponse = await getDelegatedClaimsDocument({
          id: delegationId,
        })
        // console.log('documentResponse', documentResponse)
        if (documentResponse?.data) {
          setDocumentData(documentResponse.data)
        }

        if (response?.data) {
          const {
            delegationInfo: delegationData,
            prescriptions: prescriptionsData,
          } = response.data

          // 위임 정보 매핑
          if (delegationData) {
            const mappedDelegationInfo = {
              id: delegationData.id,
              delegationType: (() => {
                if (delegationData.applicantRelation === 'SELF') {
                  return '본인'
                }
                return '법정 대리인'
              })(),
              applicant:
                delegationData.applicantName || delegationData.patientName,
              recipient: delegationData.patientName,
              age: delegationData.patientBirthday
                ? calculateAgeCategory(delegationData.patientBirthday)
                : '-',
              applicationDate: delegationData.createdAt
                ? dayjs(delegationData.createdAt).format('YYYY-MM-DD')
                : '-',
              delegationClaimsExpiryDate: delegationData.delegationExpiredAt
                ? dayjs(delegationData.delegationExpiredAt).format('YYYY-MM-DD')
                : '-',
              status: delegationData.status,
            }
            setDelegationInfo(mappedDelegationInfo)
          }

          // 처방전 목록 매핑
          if (prescriptionsData?.content) {
            const mappedPrescriptionList = prescriptionsData.content.map(
              (prescription, index) => ({
                key: index.toString(),
                id: prescription.id,
                orderNumber: prescription.orderId
                  ? `SG${prescription.orderId}`
                  : '-',
                purchaseDate: prescription.createdAt
                  ? dayjs(prescription.createdAt).format('YYYY-MM-DD')
                  : '-',
                status: prescription.status,
                image_url: prescription.imageUrl || null,
                created_at: prescription.createdAt
                  ? dayjs(prescription.createdAt).format('YYYY-MM-DD HH:mm:ss')
                  : '-',
              }),
            )
            setPrescriptionList(mappedPrescriptionList)
          } else {
            setPrescriptionList([])
          }
        }
      } catch (error) {
        console.error('위임 상세 정보 조회 실패:', error)
        alert('위임 상세 정보를 불러오는데 실패했습니다.')
      } finally {
        setLoading(false)
      }
    }

    fetchDelegationDetail()
  }, [delegationId])

  // 뒤로 가기
  const handleGoBack = () => {
    navigate('/delegation-claims')
  }

  // 처방전 목록 테이블 컬럼
  const prescriptionColumns = [
    {
      title: '상태값',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => {
        const statusConfig = {
          SUBMITTED: { text: '처방전 제출', color: '#F59E0B' },
          PURCHASED: { text: '구매완료', color: '#117FFA' },
          ADMIN_APPROVED: { text: '관리자 승인', color: '#00A4FF' },
          ADMIN_REJECTED: { text: '관리자 반려', color: '#EB516F' },
          REJECTED: { text: '공단 반려', color: '#EB516F' },
          FINALIZED: { text: '최종 완료', color: '#00A4FF' },
        }

        const config = statusConfig[status] || {
          text: status,
          color: '#6D747A',
        }

        return (
          <Tag
            color={config.color}
            style={{ color: '#FFF', fontWeight: 'bold' }}
          >
            {config.text}
          </Tag>
        )
      },
    },
    {
      title: '주문번호',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      width: 150,
    },
    {
      title: '구매일자',
      dataIndex: 'purchaseDate',
      key: 'purchaseDate',
      width: 120,
    },
    {
      title: '생성일시',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (createdAt) => (
        <span style={{ fontSize: '12px', color: '#6D747A' }}>{createdAt}</span>
      ),
    },
  ]

  if (loading) {
    return (
      <div
        style={{
          padding: '20px',
          textAlign: 'center',
          minHeight: '400px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spin size="large" />
      </div>
    )
  }

  if (!delegationInfo) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>위임 정보를 찾을 수 없습니다.</div>
        <Button onClick={handleGoBack} style={{ marginTop: '16px' }}>
          목록으로 돌아가기
        </Button>
      </div>
    )
  }

  const renderStatusTag = () => {
    const statusConfig = {
      SUBMITTED: {
        text: '위임장 심사 대기',
        color: '#117FFA',
      },
      APPROVED: {
        text: '위임 완료',
        color: '#00A4FF',
      },
      REJECTED: {
        text: '반려',
        color: '#EB516F',
      },
    }

    const config = statusConfig[delegationInfo.status] || {
      text: '알 수 없음',
      color: '#6D747A',
    }

    return (
      <Tag color={config.color} style={{ color: '#FFF', fontWeight: 'bold' }}>
        {config.text}
      </Tag>
    )
  }

  const renderDelegationTypeTag = () => {
    const isPersonal = delegationInfo.delegationType === '본인'

    return (
      <Tag
        color={isPersonal ? '#00A4FF' : '#115CB0'}
        style={{ color: '#FFF', fontWeight: 'bold', marginLeft: '8px' }}
      >
        {delegationInfo.delegationType}
      </Tag>
    )
  }

  const infoItemStyle = {
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
  }

  const labelStyle = {
    color: '#191a1c',
    fontWeight: 'bold',
    minWidth: '120px',
    marginRight: '8px',
  }

  const valueStyle = {
    color: '#6D747A',
  }

  return (
    <div
      style={{
        padding: '20px',
        backgroundColor: '#F6F7FA',
        minHeight: '100vh',
      }}
    >
      {/* 페이지 헤더 */}
      <h1
        style={{
          color: '#191a1c',
          margin: '0px 0px 20px 0px',
          fontSize: '28px',
          fontWeight: 'bold',
        }}
      >
        위임 상세 정보
      </h1>

      {/* 위임 기본 정보 카드 */}
      <Card
        title={
          <span
            style={{ fontSize: '18px', fontWeight: 'bold', color: '#191a1c' }}
          >
            📋 위임 기본 정보
          </span>
        }
        style={{
          marginBottom: '24px',
          borderRadius: '12px',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
          border: 'none',
        }}
        bodyStyle={{ padding: '32px' }}
      >
        <Row gutter={[40, 24]}>
          <Col span={12}>
            <div style={{ lineHeight: '2' }}>
              <div style={infoItemStyle}>
                <span style={labelStyle}>위임번호:</span>
                <span style={valueStyle}>{delegationInfo.id}</span>
              </div>

              <div style={infoItemStyle}>
                <span style={labelStyle}>위임타입:</span>
                {renderDelegationTypeTag()}
              </div>

              <div style={infoItemStyle}>
                <span style={labelStyle}>신청자:</span>
                <span style={valueStyle}>{delegationInfo.applicant}</span>
              </div>

              <div style={infoItemStyle}>
                <span style={labelStyle}>수진자:</span>
                <span style={valueStyle}>{delegationInfo.recipient}</span>
              </div>
            </div>
          </Col>

          <Col span={12}>
            <div style={{ lineHeight: '2' }}>
              <div style={infoItemStyle}>
                <span style={labelStyle}>나이:</span>
                <span style={valueStyle}>{delegationInfo.age}</span>
              </div>

              <div style={infoItemStyle}>
                <span style={labelStyle}>신청일자:</span>
                <span style={valueStyle}>{delegationInfo.applicationDate}</span>
              </div>

              <div style={infoItemStyle}>
                <span style={labelStyle}>만료일자:</span>
                <span style={valueStyle}>
                  {delegationInfo.delegationClaimsExpiryDate}
                </span>
              </div>

              <div style={infoItemStyle}>
                <span style={labelStyle}>현재 상태:</span>
                {renderStatusTag()}
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* 위임장 문서 카드 */}
      {documentData && (
        <Card
          title={
            <span
              style={{ fontSize: '18px', fontWeight: 'bold', color: '#191a1c' }}
            >
              📄 위임장 문서
            </span>
          }
          style={{
            marginBottom: '24px',
            borderRadius: '12px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
            border: 'none',
          }}
          bodyStyle={{ padding: '32px' }}
        >
          <Row gutter={[24, 16]}>
            {/* 위임장 문서 */}
            <Col span={12}>
              <div style={{ marginBottom: '16px' }}>
                <div
                  style={{
                    marginBottom: '8px',
                    fontWeight: 'bold',
                    color: '#191a1c',
                  }}
                >
                  위임장 문서
                </div>
                {documentData.document ? (
                  <Button
                    type="primary"
                    onClick={() => window.open(documentData.document, '_blank')}
                    style={{
                      backgroundColor: '#117FFA',
                      borderColor: '#117FFA',
                    }}
                  >
                    위임장 문서 다운로드
                  </Button>
                ) : (
                  <span style={{ color: '#A4ADB4' }}>위임장 문서 없음</span>
                )}
              </div>
            </Col>

            {/* 첨부 서류 */}
            <Col span={12}>
              <div style={{ marginBottom: '16px' }}>
                <div
                  style={{
                    marginBottom: '8px',
                    fontWeight: 'bold',
                    color: '#191a1c',
                  }}
                >
                  첨부 서류
                </div>
                {documentData.attachments &&
                documentData.attachments.length > 0 ? (
                  <div>
                    {documentData.attachments.map((attachment, index) => (
                      <div key={index} style={{ marginBottom: '8px' }}>
                        <Button
                          type="link"
                          onClick={() =>
                            window.open(attachment.file.downloadUrl, '_blank')
                          }
                          style={{
                            padding: 0,
                            height: 'auto',
                            color: '#117FFA',
                          }}
                        >
                          📎 {attachment.name}
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span style={{ color: '#A4ADB4' }}>첨부 서류 없음</span>
                )}
              </div>
            </Col>
          </Row>
        </Card>
      )}

      {/* 처방전 목록 카드 */}
      <Card
        title={
          <span
            style={{ fontSize: '18px', fontWeight: 'bold', color: '#191a1c' }}
          >
            💊 처방전 목록 ({prescriptionList.length}건)
          </span>
        }
        style={{
          borderRadius: '12px',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
          border: 'none',
        }}
        bodyStyle={{ padding: '24px' }}
      >
        <Table
          columns={prescriptionColumns}
          dataSource={prescriptionList}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}건`,
            style: { marginTop: '16px' },
          }}
          scroll={{ x: 900 }}
          style={{
            backgroundColor: '#FFF',
            borderRadius: '8px',
          }}
        />
      </Card>
    </div>
  )
}

export default DelegatedClaimsDetailPage
